<template>
  <section class="reward-admin">
    <div class="admin-block">
      <header class="block-header">
        <h2>{{ $t('referral.rewardAdmin.shareTitle') }}</h2>
        <div class="segmented">
          <button type="button" :class="{ active: statusFilter === 'pending' }" @click="setStatusFilter('pending')">
            {{ $t('referral.rewardAdmin.filterPending') }}
          </button>
          <button type="button" :class="{ active: statusFilter === null }" @click="setStatusFilter(null)">
            {{ $t('referral.rewardAdmin.filterAll') }}
          </button>
        </div>
        <span class="record-count">{{ $t('referral.admin.total', { count: submissions.length }) }}</span>
      </header>

      <p v-if="shareError" class="load-error">{{ shareError }}</p>

      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{{ $t('referral.rewardAdmin.user') }}</th>
              <th>{{ $t('referral.rewardAdmin.post') }}</th>
              <th>{{ $t('referral.rewardAdmin.submittedAt') }}</th>
              <th>{{ $t('referral.rewardAdmin.status') }}</th>
              <th>{{ $t('referral.rewardAdmin.action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!submissions.length && !isLoadingShares">
              <td colspan="5" class="empty-cell">{{ $t('referral.rewardAdmin.shareEmpty') }}</td>
            </tr>
            <tr v-for="item in submissions" :key="item.uid">
              <td>
                {{ item.email }}
                <span class="mono muted">{{ item.uid }}</span>
              </td>
              <td class="post-cell">
                <span>{{ $t(`referral.share.platforms.${item.platform}`) }}</span>
                <a :href="item.postUrl" target="_blank" rel="noopener noreferrer" class="mono">{{ item.postUrl }}</a>
              </td>
              <td class="mono">
                {{ formatTime(item.submittedAt) }}
                <span class="muted">{{ daysSince(item.submittedAt) }}d</span>
              </td>
              <td>
                {{ statusLabel(item.status) }}
                <span v-if="item.status === 'approved'" class="mono muted">+{{ item.reward }} · {{ $t('referral.rewardAdmin.engagementShort', { count: item.engagement }) }}</span>
                <span v-if="item.rejectReason" class="muted">{{ item.rejectReason }}</span>
              </td>
              <td>
                <div v-if="item.status === 'pending'" class="review-actions">
                  <label class="engagement-field">
                    <span>{{ $t('referral.rewardAdmin.engagement') }}</span>
                    <input
                      v-model.number="engagementDrafts[item.uid]"
                      type="number"
                      min="0"
                      class="reward-input"
                    >
                  </label>
                  <span class="mono muted">= {{ rewardFor(engagementDrafts[item.uid]) }}</span>
                  <button type="button" class="filter-btn" :disabled="reviewingUid === item.uid" @click="review(item, 'approve')">
                    {{ $t('referral.rewardAdmin.approve') }}
                  </button>
                  <button type="button" class="filter-btn danger" :disabled="reviewingUid === item.uid" @click="review(item, 'reject')">
                    {{ $t('referral.rewardAdmin.reject') }}
                  </button>
                </div>
                <span v-else class="mono muted">{{ formatTime(item.reviewedAt) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="admin-block">
      <header class="block-header">
        <h2>{{ $t('referral.rewardAdmin.codesTitle') }}</h2>
      </header>

      <form class="admin-filter" @submit.prevent="generateCodes">
        <label class="field">
          <span>{{ $t('referral.rewardAdmin.credits') }}</span>
          <input v-model.number="newCodes.credits" type="number" min="1" required>
        </label>
        <label class="field">
          <span>{{ $t('referral.rewardAdmin.count') }}</span>
          <input v-model.number="newCodes.count" type="number" min="1" max="100" required>
        </label>
        <label class="field">
          <span>{{ $t('referral.rewardAdmin.maxRedemptions') }}</span>
          <input v-model.number="newCodes.maxRedemptions" type="number" min="1" required>
        </label>
        <label class="field note-field">
          <span>{{ $t('referral.rewardAdmin.note') }}</span>
          <input v-model="newCodes.note" type="text">
        </label>
        <button type="submit" class="filter-btn" :disabled="isGenerating">{{ $t('referral.rewardAdmin.generate') }}</button>
      </form>

      <div v-if="generatedCodes.length" class="generated">
        <span>{{ $t('referral.rewardAdmin.generated', { count: generatedCodes.length }) }}</span>
        <code class="mono">{{ generatedCodes.join(' ') }}</code>
        <button type="button" class="filter-btn" @click="copyGenerated">{{ $t('referral.rewardAdmin.copyAll') }}</button>
      </div>

      <p v-if="codesError" class="load-error">{{ codesError }}</p>

      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{{ $t('referral.rewardAdmin.code') }}</th>
              <th>{{ $t('referral.rewardAdmin.credits') }}</th>
              <th>{{ $t('referral.rewardAdmin.used') }}</th>
              <th>{{ $t('referral.rewardAdmin.note') }}</th>
              <th>{{ $t('referral.rewardAdmin.createdAt') }}</th>
              <th>{{ $t('referral.rewardAdmin.action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!codes.length && !isLoadingCodes">
              <td colspan="6" class="empty-cell">{{ $t('referral.rewardAdmin.codesEmpty') }}</td>
            </tr>
            <tr v-for="item in codes" :key="item.code" :class="{ 'is-disabled': item.disabled }">
              <td class="mono">
                {{ item.code }}
                <span v-if="item.disabled" class="muted">{{ $t('referral.rewardAdmin.disabled') }}</span>
              </td>
              <td class="mono">{{ item.credits }}</td>
              <td>
                <span class="mono">{{ item.redeemedCount }} / {{ item.maxRedemptions }}</span>
                <button
                  v-if="item.redeemedCount"
                  type="button"
                  class="email-link"
                  @click="toggleRedemptions(item.code)"
                >
                  {{ $t('referral.rewardAdmin.viewRedemptions') }}
                </button>
                <template v-if="redemptions[item.code]">
                  <span v-if="!redemptions[item.code].length" class="muted">{{ $t('referral.rewardAdmin.redemptionsEmpty') }}</span>
                  <span v-for="record in redemptions[item.code]" :key="record.uid" class="muted">
                    {{ record.email }} · {{ formatTime(record.redeemedAt) }}
                  </span>
                </template>
              </td>
              <td>{{ item.note }}</td>
              <td class="mono">{{ formatTime(item.createdAt) }}</td>
              <td>
                <button type="button" class="filter-btn" :class="{ danger: !item.disabled }" @click="toggleDisabled(item)">
                  {{ item.disabled ? $t('referral.rewardAdmin.enable') : $t('referral.rewardAdmin.disable') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { ElMessage } from 'element-plus'
import { apiPost } from '@/composables/useApi'

export default {
  name: 'GiftRewardAdminPanel',
  props: {
    adminPassword: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      submissions: [],
      statusFilter: 'pending',
      engagementDrafts: {},
      rewardRule: { baseReward: 0, perEngagement: 0 },
      reviewingUid: '',
      isLoadingShares: false,
      shareError: '',
      codes: [],
      redemptions: {},
      newCodes: { credits: 2000, count: 1, maxRedemptions: 1, note: '' },
      generatedCodes: [],
      isGenerating: false,
      isLoadingCodes: false,
      codesError: ''
    }
  },
  mounted() {
    this.loadSubmissions()
    this.loadCodes()
  },
  methods: {
    adminPost(url, body = {}) {
      return apiPost(url, { adminPassword: this.adminPassword, ...body }, { auth: false })
    },
    async loadSubmissions() {
      this.isLoadingShares = true
      this.shareError = ''
      try {
        const data = await this.adminPost('/api/admin/share-rewards', { status: this.statusFilter })
        this.submissions = data.records
        this.rewardRule = { baseReward: data.baseReward, perEngagement: data.perEngagement }
        this.engagementDrafts = Object.fromEntries(data.records.map((item) => [item.uid, 0]))
      } catch (error) {
        this.shareError = error.message
      } finally {
        this.isLoadingShares = false
      }
    },
    setStatusFilter(status) {
      this.statusFilter = status
      this.loadSubmissions()
    },
    async review(item, action) {
      let reason = ''
      if (action === 'reject') {
        reason = window.prompt(this.$t('referral.rewardAdmin.rejectPrompt'))
        if (reason === null) return
      }
      this.reviewingUid = item.uid
      this.shareError = ''
      try {
        await this.adminPost('/api/admin/share-rewards/review', {
          uid: item.uid,
          action,
          engagement: this.engagementDrafts[item.uid] || 0,
          reason
        })
        await this.loadSubmissions()
      } catch (error) {
        this.shareError = error.message
      } finally {
        this.reviewingUid = ''
      }
    },
    async loadCodes() {
      this.isLoadingCodes = true
      this.codesError = ''
      try {
        const data = await this.adminPost('/api/admin/gift-codes')
        this.codes = data.codes
      } catch (error) {
        this.codesError = error.message
      } finally {
        this.isLoadingCodes = false
      }
    },
    async generateCodes() {
      this.isGenerating = true
      this.codesError = ''
      try {
        const data = await this.adminPost('/api/admin/gift-codes/create', this.newCodes)
        this.generatedCodes = data.codes
        await this.loadCodes()
      } catch (error) {
        this.codesError = error.message
      } finally {
        this.isGenerating = false
      }
    },
    async copyGenerated() {
      await navigator.clipboard.writeText(this.generatedCodes.join('\n'))
      ElMessage.success(this.$t('referral.copied'))
    },
    async toggleDisabled(item) {
      this.codesError = ''
      try {
        await this.adminPost('/api/admin/gift-codes/disable', { code: item.code, disabled: !item.disabled })
        item.disabled = !item.disabled
      } catch (error) {
        this.codesError = error.message
      }
    },
    async toggleRedemptions(code) {
      if (this.redemptions[code]) {
        delete this.redemptions[code]
        return
      }
      const data = await this.adminPost('/api/admin/gift-codes/redemptions', { code })
      this.redemptions[code] = data.records
    },
    rewardFor(engagement) {
      return this.rewardRule.baseReward + this.rewardRule.perEngagement * (Number(engagement) || 0)
    },
    statusLabel(status) {
      return {
        pending: this.$t('referral.rewardAdmin.statusPending'),
        approved: this.$t('referral.rewardAdmin.statusApproved'),
        rejected: this.$t('referral.rewardAdmin.statusRejected')
      }[status]
    },
    daysSince(value) {
      return Math.floor((Date.now() - new Date(value).getTime()) / 86400000)
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : ''
    }
  }
}
</script>

<style scoped>
.reward-admin {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

.admin-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.block-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.block-header h2 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.segmented {
  display: inline-flex;
  padding: 2px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
}

.segmented button {
  height: 30px;
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.segmented button.active {
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-weight: 600;
}

.segmented button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.admin-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.field input,
.reward-input {
  width: 120px;
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
}

.note-field input {
  width: 220px;
}

.reward-input {
  width: 84px;
  font-family: var(--font-family-data);
}

.field input:focus-visible,
.reward-input:focus-visible {
  outline: none;
  border-color: var(--workspace-accent-dark);
  box-shadow: var(--focus-ring-brand);
}

.filter-btn {
  height: 36px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}

.filter-btn:hover:not(:disabled) {
  background: var(--surface-subtle);
}

.filter-btn.danger {
  color: var(--color-danger);
}

.filter-btn:disabled {
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.filter-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.engagement-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.review-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.generated {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.generated code {
  flex: 1 1 320px;
  color: var(--ink-primary);
  word-break: break-all;
}

.record-count {
  margin-left: auto;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.load-error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

th,
td {
  padding: var(--space-3);
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  vertical-align: top;
}

th {
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  font-weight: 600;
  white-space: nowrap;
}

tbody tr:last-child td {
  border-bottom: 0;
}

td span {
  display: block;
}

.post-cell {
  max-width: 360px;
}

.post-cell a {
  display: block;
  overflow: hidden;
  color: var(--workspace-accent-dark);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mono {
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.muted {
  color: var(--ink-tertiary);
}

.email-link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--workspace-accent-dark);
  font: inherit;
  font-size: var(--font-size-xs);
  text-align: left;
  cursor: pointer;
}

.email-link:hover {
  text-decoration: underline;
}

.email-link:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

tr.is-disabled td {
  color: var(--ink-tertiary);
}

.empty-cell {
  padding: var(--space-8);
  color: var(--ink-secondary);
  text-align: center;
}
</style>
